import type { Locale } from "./config";

/**
 * Localized URL segments for Spanish and Portuguese (English keeps the route names). Routes are
 * written in code with their internal segments (/leaderboards/ai/reasoning) and published with the
 * reader's words (/rankings/ia/razonamiento, /pt/rankings/ia/raciocinio): localePath() translates,
 * next.config rewrites the public path back to the route and 301-redirects the old English-segment
 * URLs. Segments not listed here (product ids, article slugs) pass through unchanged.
 */
type Node = { es: string; pt: string; children?: Record<string, Node> };
type Tree = Record<string, Node>;
const n = (es: string, pt: string = es, children?: Record<string, Node>): Node => ({ es, pt, ...(children && { children }) });

const VIEWS: Record<string, Node> = {
  overall: n("general", "geral"), reasoning: n("razonamiento", "raciocinio"), coding: n("programacion", "programacao"),
  value: n("costo-beneficio", "custo-beneficio"), price: n("precio", "preco"), context: n("contexto-largo", "contexto-longo"),
  "coding-value": n("codigo-por-dolar", "codigo-por-dolar"), "coding-budget": n("codigo-con-presupuesto", "codigo-com-orcamento"),
  finance: n("finanzas", "financas"), legal: n("legal", "juridico"), healthcare: n("salud", "saude"),
  support: n("atencion-al-cliente", "atendimento-ao-cliente"), software: n("software"), research: n("investigacion", "pesquisa"),
};
const categories = (ai?: Record<string, Node>): Record<string, Node> => ({
  ai: n("ia", "ia", ai), cloud: n("cloud"), databases: n("bases-de-datos", "bancos-de-dados"),
  embeddings: n("embeddings"), storage: n("almacenamiento", "armazenamento"), queues: n("colas", "filas"),
});

export const SEGMENTS: Tree = {
  leaderboards: n("rankings", "rankings", categories(VIEWS)),
  compare: n("comparar", "comparar", categories()),
  catalog: n("catalogo", "catalogo", categories()),
  solutions: n("soluciones", "solucoes"),
  industries: n("industrias", "setores", { finance: n("finanzas", "financas"), software: n("software"), retail: n("retail", "varejo"), legal: n("legal", "juridico"), healthcare: n("salud", "saude") }),
  "use-cases": n("casos-de-uso", "casos-de-uso", {
    "reduce-ai-costs": n("reducir-costos-de-ia", "reduzir-custos-de-ia"), "ai-customer-support": n("atencion-al-cliente-con-ia", "atendimento-com-ia"),
    "rag-document-search": n("busqueda-y-rag", "busca-e-rag"), "ai-coding": n("programar-con-ia", "programar-com-ia"),
    "cut-cloud-costs": n("bajar-factura-cloud", "reduzir-conta-cloud"), "handle-traffic-spikes": n("picos-de-trafico", "picos-de-trafego"),
    "ship-to-production": n("llevar-a-produccion", "levar-para-producao"),
    "choose-a-database": n("elegir-base-de-datos", "escolher-banco-de-dados"), "launch-an-mvp": n("lanzar-un-mvp", "lancar-um-mvp"),
    "evaluate-new-ai-providers": n("evaluar-proveedores-de-ia", "avaliar-provedores-de-ia"),
    "ci-cd-pipelines": n("ci-cd-github-a-produccion", "ci-cd-github-para-producao"), "secrets-and-environments": n("secretos-y-ambientes", "segredos-e-ambientes"),
    "observability-without-the-bill": n("observabilidad-sin-sorpresas", "observabilidade-sem-surpresas"), "background-jobs-and-queues": n("colas-y-trabajos-en-segundo-plano", "filas-e-tarefas-em-segundo-plano"),
    "file-uploads": n("subida-de-archivos", "upload-de-arquivos"), "infrastructure-as-code": n("infraestructura-como-codigo", "infraestrutura-como-codigo"),
    "postgres-performance": n("postgresql-rapido", "postgresql-rapido"), "kubernetes-or-paas": n("kubernetes-o-paas", "kubernetes-ou-paas"),
  }),
  articles: n("articulos", "artigos"),
  research: n("biblioteca", "biblioteca"),
  guides: n("guias", "guias"),
  methodology: n("metodologia", "metodologia", { catalog: n("catalogo") }),
  tools: n("herramientas", "ferramentas", { "ai-cost-calculator": n("calculadora-costos-ia", "calculadora-custos-ia"), "aws-savings-calculator": n("calculadora-ahorro-aws", "calculadora-economia-aws") }),
  analysis: n("analisis", "analise", { "cloudops-platforms": n("plataformas-cloudops") }),
  advisory: n("asesoria", "consultoria"),
  "open-data": n("datos-abiertos", "dados-abertos"),
  contact: n("contacto", "contato"),
  account: n("cuenta", "conta"),
  privacy: n("privacidad", "privacidade"),
  terms: n("terminos", "termos"),
};

type Localized = Exclude<Locale, "en">;
const translatable = (locale: Locale): locale is Localized => locale !== "en";

/** /leaderboards/ai/reasoning → /rankings/ia/razonamiento (a bare path, without language prefix). */
export function localizeSegments(locale: Locale, path: string) {
  if (!translatable(locale) || path === "/") return path;
  let level: Tree | undefined = SEGMENTS;
  return path.split("/").map((segment, i) => {
    if (i === 0) return segment;
    const node: Node | undefined = level?.[segment];
    level = node?.children;
    return node ? node[locale] : segment;
  }).join("/");
}

/** The reverse: /rankings/ia/razonamiento → /leaderboards/ai/reasoning. Internal segments are accepted too. */
export function internalSegments(locale: Locale, path: string) {
  if (!translatable(locale) || path === "/") return path;
  let level: Tree | undefined = SEGMENTS;
  return path.split("/").map((segment, i) => {
    if (i === 0) return segment;
    const entry = level && (Object.entries(level).find(([, node]) => node[locale] === segment) ?? (level[segment] ? [segment, level[segment]] as const : undefined));
    level = entry?.[1].children;
    return entry ? entry[0] : segment;
  }).join("/");
}

type Rule = { source: string; destination: string };
/** Every translated path of a locale, deepest first, with a trailing wildcard for dynamic children. */
function pairs(locale: Localized): Rule[] {
  const prefix = locale === "es" ? "" : `/${locale}`;
  const out: Rule[] = [];
  const walk = (tree: Tree, internal: string, published: string) => {
    for (const [key, node] of Object.entries(tree)) {
      const i = `${internal}/${key}`, p = `${published}/${node[locale]}`;
      if (node.children) walk(node.children, i, p);
      if (p !== i) out.push({ source: `${prefix}${p}/:rest*`, destination: `${prefix}${i}/:rest*` }, { source: `${prefix}${p}`, destination: `${prefix}${i}` });
    }
  };
  walk(SEGMENTS, "", "");
  // Paths whose own segment is unchanged but a descendant is translated still need their children mapped.
  return out;
}

/** next.config rewrites: public (localized) path → the route that renders it. */
export const localizedRewrites = () => (["es", "pt"] as const).flatMap(pairs);
/** next.config redirects: the old English-segment URL → its localized one (301). */
export const localizedRedirects = () => localizedRewrites().map(({ source, destination }) => ({ source: destination, destination: source, permanent: true }));
